import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "@/components/Account";
import { authAPI } from "@/lib/api";

const timelineMonths = {
  "0_3": 3,
  "3_6": 6,
  "6_12": 12,
  exploring: 24,
};

const defaults = {
  journey_type: "first_home",
  preferred_suburbs: "",
  budget_min: "",
  budget_max: "",
  min_bedrooms: "3",
  timeframe: "3_6",
  finance_status: "not_started",
  buying_with: "myself",
  first_home_support: "unsure",
  selling_existing: "no",
  home_priorities: "",
  portfolio_properties: "0",
  investment_strategy: "balanced",
  target_weekly_rent: "",
};

function initialValues(profile) {
  const preferences = profile?.preferences || {};
  const buyer = preferences.role_details?.buyer || {};
  const answers = preferences.onboarding_answers || {};
  return {
    ...defaults,
    ...answers,
    journey_type: preferences.journey_type || defaults.journey_type,
    preferred_suburbs: buyer.preferred_suburbs || "",
    budget_min: buyer.budget_min ?? "",
    budget_max: buyer.budget_max ?? "",
    min_bedrooms: buyer.min_bedrooms ?? "3",
    timeframe:
      Object.entries(timelineMonths).find(
        ([, months]) => months === buyer.timeline_months,
      )?.[0] || defaults.timeframe,
  };
}

export default function Onboarding() {
  const { user, profile, setProfile, loading, setDraft } = useSession();
  const navigate = useNavigate();
  const startingValues = useMemo(() => initialValues(profile), [profile]);
  const [values, setValues] = useState(startingValues);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setValues(startingValues), [startingValues]);

  if (loading) return <p>Checking your session…</p>;
  if (!user)
    return (
      <section className="research-card onboarding-shell">
        <h1>Tell us what you are planning</h1>
        <p>
          <Link to="/account">Sign in or create an account</Link> to save your
          preferences.
        </p>
      </section>
    );

  const update = (event) =>
    setValues({ ...values, [event.target.name]: event.target.value });

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const budgetMin = Number(values.budget_min);
    const budgetMax = Number(values.budget_max);
    if (budgetMax < budgetMin) {
      setError("Your maximum budget needs to be at least your minimum budget.");
      setBusy(false);
      return;
    }
    const journeyLabels = {
      first_home: "First home",
      owner_occupier: "Owner occupier",
      investor: `Investor · ${values.investment_strategy}`,
    };
    try {
      const response = await authAPI.updateProfile({
        onboarding_completed: true,
        preferences: {
          ...(profile?.preferences || {}),
          journey_type: values.journey_type,
          onboarding_answers: {
            finance_status: values.finance_status,
            buying_with: values.buying_with,
            first_home_support: values.first_home_support,
            selling_existing: values.selling_existing,
            home_priorities: values.home_priorities.trim(),
            portfolio_properties: Number(values.portfolio_properties || 0),
            investment_strategy: values.investment_strategy,
            target_weekly_rent: values.target_weekly_rent
              ? Number(values.target_weekly_rent)
              : null,
          },
          role_details: {
            ...(profile?.preferences?.role_details || {}),
            buyer: {
              budget_min: budgetMin,
              budget_max: budgetMax,
              preferred_suburbs:
                values.preferred_suburbs.trim() || "Still exploring",
              min_bedrooms: Number(values.min_bedrooms),
              investment_goal: journeyLabels[values.journey_type],
              timeline_months: timelineMonths[values.timeframe],
            },
          },
        },
      });
      setProfile(response.data);
      setDraft((current) => ({
        ...(current || {
          address: "",
          price: "",
          property_type: "house",
          postcode: "",
          address_id: null,
        }),
        goal: values.journey_type === "investor" ? "invest" : "live_in",
        bedrooms: values.min_bedrooms,
        confirmed: false,
      }));
      navigate("/research");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "We could not save your preferences. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="onboarding-shell">
      <div className="intro">
        <span className="eyebrow">A research experience shaped around you</span>
        <h1>What are you planning?</h1>
        <p>
          We use these answers to set the order and starting assumptions in your
          research. You can change them from your account at any time.
        </p>
      </div>
      <form className="research-card onboarding-form" onSubmit={submit}>
        <fieldset className="choice-fieldset">
          <legend>I am looking for</legend>
          <div className="journey-options">
            {[
              ["first_home", "My first home", "Deposit, grants and upfront costs"],
              ["owner_occupier", "My next home", "Household needs and moving plans"],
              ["investor", "An investment", "Rent, yield and cash flow"],
            ].map(([value, label, detail]) => (
              <label key={value} className="journey-option">
                <input
                  type="radio"
                  name="journey_type"
                  value={value}
                  checked={values.journey_type === value}
                  onChange={update}
                />
                <span>
                  <strong>{label}</strong>
                  <small>{detail}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="form-grid">
          <label>
            Suburbs or areas you are considering
            <input
              name="preferred_suburbs"
              value={values.preferred_suburbs}
              onChange={update}
              placeholder="e.g. Chermside, Aspley, north Brisbane"
              maxLength={300}
            />
          </label>
          <label>
            Minimum bedrooms
            <select name="min_bedrooms" value={values.min_bedrooms} onChange={update}>
              {[1, 2, 3, 4, 5].map((number) => (
                <option key={number} value={number}>{number}{number === 5 ? "+" : ""}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-grid">
          <label>
            Budget from (AUD)
            <input required type="number" min="0" step="10000" name="budget_min" value={values.budget_min} onChange={update} />
          </label>
          <label>
            Budget up to (AUD)
            <input required type="number" min="0" step="10000" name="budget_max" value={values.budget_max} onChange={update} />
          </label>
        </div>
        <div className="form-grid">
          <label>
            When do you hope to buy?
            <select name="timeframe" value={values.timeframe} onChange={update}>
              <option value="0_3">Within 3 months</option>
              <option value="3_6">3–6 months</option>
              <option value="6_12">6–12 months</option>
              <option value="exploring">I am exploring</option>
            </select>
          </label>
          <label>
            Finance progress
            <select name="finance_status" value={values.finance_status} onChange={update}>
              <option value="not_started">Not started</option>
              <option value="researching">Researching lenders or brokers</option>
              <option value="pre_approved">Pre-approved</option>
              <option value="cash">Buying without finance</option>
            </select>
          </label>
        </div>

        {values.journey_type === "first_home" && (
          <section className="branch-fields" aria-labelledby="first-home-details">
            <h2 id="first-home-details">Your first-home plans</h2>
            <div className="form-grid">
              <label>
                Buying with
                <select name="buying_with" value={values.buying_with} onChange={update}>
                  <option value="myself">Myself</option>
                  <option value="partner">A partner</option>
                  <option value="family">Family or another co-buyer</option>
                </select>
              </label>
              <label>
                First-home support
                <select name="first_home_support" value={values.first_home_support} onChange={update}>
                  <option value="unsure">Show me what to check</option>
                  <option value="interested">I plan to investigate schemes</option>
                  <option value="not_needed">Not part of my plan</option>
                </select>
              </label>
            </div>
          </section>
        )}

        {values.journey_type === "owner_occupier" && (
          <section className="branch-fields" aria-labelledby="next-home-details">
            <h2 id="next-home-details">Your next-home plans</h2>
            <label>
              Do you expect to sell a property before buying?
              <select name="selling_existing" value={values.selling_existing} onChange={update}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
                <option value="unsure">Still deciding</option>
              </select>
            </label>
            <label>
              What matters most in the move? (optional)
              <input name="home_priorities" value={values.home_priorities} onChange={update} placeholder="e.g. schools, commute, accessibility, more space" maxLength={300} />
            </label>
          </section>
        )}

        {values.journey_type === "investor" && (
          <section className="branch-fields" aria-labelledby="investor-details">
            <h2 id="investor-details">Your investment approach</h2>
            <div className="form-grid">
              <label>
                Properties currently owned
                <input type="number" min="0" max="100" name="portfolio_properties" value={values.portfolio_properties} onChange={update} />
              </label>
              <label>
                Main strategy
                <select name="investment_strategy" value={values.investment_strategy} onChange={update}>
                  <option value="cash_flow">Cash flow</option>
                  <option value="balanced">Balanced</option>
                  <option value="long_term">Long-term growth</option>
                </select>
              </label>
            </div>
            <label>
              Target weekly rent (optional)
              <input type="number" min="0" step="10" name="target_weekly_rent" value={values.target_weekly_rent} onChange={update} />
            </label>
          </section>
        )}

        {error && <p className="error-state" role="alert">{error}</p>}
        <button disabled={busy}>{busy ? "Saving…" : "Save and start researching"}</button>
      </form>
    </div>
  );
}
